import { apiFetch } from '../../lib/apiClient';
import type {
  PlanStep,
  PlaybookSummary,
  StrategyActionDetail,
  StrategyDetail,
  StrategyLearningResponse,
  StrategyRoleDetail,
  UtilityUseRecord,
} from './types';

export type StrategyWrite = {
  name?: string;
  map?: string;
  side?: string;
  status?: string;
  buyType?: string;
  strategyType?: string | null;
  description?: string | null;
  site?: string | null;
  route?: string | null;
};

export type PlanStepWrite = {
  sortOrder?: number;
  title?: string;
  body?: string;
};

export type RoleWrite = {
  name?: string;
  sortOrder?: number;
  buyType?: string;
};

export type ActionWrite = {
  roundTimeSecs?: number;
  durationSecs?: number | null;
  actionType?: string;
  description?: string | null;
  utilityUseId?: string | null;
};

export type UtilityWrite = {
  map?: string;
  name?: string;
  description?: string | null;
  url?: string | null;
  utilityType?: string;
};

function strategyBase(teamId: string, strategyId: string) {
  return `/teams/${encodeURIComponent(teamId)}/strategies/${encodeURIComponent(strategyId)}`;
}

export function listStrategies(teamId: string): Promise<PlaybookSummary[]> {
  return apiFetch<PlaybookSummary[]>(`/teams/${encodeURIComponent(teamId)}/strategies`);
}

export function getStrategy(teamId: string, strategyId: string): Promise<StrategyDetail> {
  return apiFetch<StrategyDetail>(strategyBase(teamId, strategyId));
}

export function markRoleLearned(
  teamId: string,
  strategyId: string,
  roleId: string,
): Promise<StrategyLearningResponse> {
  return apiFetch<StrategyLearningResponse>(
    `${strategyBase(teamId, strategyId)}/roles/${encodeURIComponent(roleId)}/learning`,
    { method: 'PUT' },
  );
}

export function unmarkRoleLearned(
  teamId: string,
  strategyId: string,
  roleId: string,
): Promise<StrategyLearningResponse> {
  return apiFetch<StrategyLearningResponse>(
    `${strategyBase(teamId, strategyId)}/roles/${encodeURIComponent(roleId)}/learning`,
    { method: 'DELETE' },
  );
}

export function setStrategyLearning(
  teamId: string,
  strategyId: string,
  learned: boolean,
): Promise<StrategyLearningResponse> {
  return apiFetch<StrategyLearningResponse>(`${strategyBase(teamId, strategyId)}/learning`, {
    method: 'PUT',
    body: { learned },
  });
}

export function createStrategy(teamId: string, body: StrategyWrite = {}): Promise<StrategyDetail> {
  return apiFetch<StrategyDetail>(`/teams/${encodeURIComponent(teamId)}/strategies`, {
    method: 'POST',
    body,
  });
}

export function updateStrategy(
  teamId: string,
  strategyId: string,
  body: StrategyWrite,
): Promise<StrategyDetail> {
  return apiFetch<StrategyDetail>(strategyBase(teamId, strategyId), { method: 'PUT', body });
}

export function duplicateStrategy(teamId: string, strategyId: string): Promise<StrategyDetail> {
  return apiFetch<StrategyDetail>(`${strategyBase(teamId, strategyId)}/duplicate`, { method: 'POST' });
}

export function deleteStrategy(teamId: string, strategyId: string): Promise<void> {
  return apiFetch<void>(strategyBase(teamId, strategyId), { method: 'DELETE' });
}

export function createPlanStep(
  teamId: string,
  strategyId: string,
  body: PlanStepWrite,
): Promise<PlanStep> {
  return apiFetch<PlanStep>(`${strategyBase(teamId, strategyId)}/plan-steps`, { method: 'POST', body });
}

export function updatePlanStep(
  teamId: string,
  strategyId: string,
  stepId: string,
  body: PlanStepWrite,
): Promise<PlanStep> {
  return apiFetch<PlanStep>(
    `${strategyBase(teamId, strategyId)}/plan-steps/${encodeURIComponent(stepId)}`,
    { method: 'PUT', body },
  );
}

export function deletePlanStep(teamId: string, strategyId: string, stepId: string): Promise<void> {
  return apiFetch<void>(
    `${strategyBase(teamId, strategyId)}/plan-steps/${encodeURIComponent(stepId)}`,
    { method: 'DELETE' },
  );
}

export function createRole(teamId: string, strategyId: string, body: RoleWrite): Promise<StrategyRoleDetail> {
  return apiFetch<StrategyRoleDetail>(`${strategyBase(teamId, strategyId)}/roles`, { method: 'POST', body });
}

export function updateRole(
  teamId: string,
  strategyId: string,
  roleId: string,
  body: RoleWrite,
): Promise<StrategyRoleDetail> {
  return apiFetch<StrategyRoleDetail>(
    `${strategyBase(teamId, strategyId)}/roles/${encodeURIComponent(roleId)}`,
    { method: 'PUT', body },
  );
}

export function deleteRole(teamId: string, strategyId: string, roleId: string): Promise<void> {
  return apiFetch<void>(
    `${strategyBase(teamId, strategyId)}/roles/${encodeURIComponent(roleId)}`,
    { method: 'DELETE' },
  );
}

function actionBase(teamId: string, strategyId: string, roleId: string) {
  return `${strategyBase(teamId, strategyId)}/roles/${encodeURIComponent(roleId)}/actions`;
}

export function createAction(
  teamId: string,
  strategyId: string,
  roleId: string,
  body: ActionWrite,
): Promise<StrategyActionDetail> {
  return apiFetch<StrategyActionDetail>(actionBase(teamId, strategyId, roleId), { method: 'POST', body });
}

export function updateAction(
  teamId: string,
  strategyId: string,
  roleId: string,
  actionId: string,
  body: ActionWrite,
): Promise<StrategyActionDetail> {
  return apiFetch<StrategyActionDetail>(
    `${actionBase(teamId, strategyId, roleId)}/${encodeURIComponent(actionId)}`,
    { method: 'PUT', body },
  );
}

export function deleteAction(
  teamId: string,
  strategyId: string,
  roleId: string,
  actionId: string,
): Promise<void> {
  return apiFetch<void>(`${actionBase(teamId, strategyId, roleId)}/${encodeURIComponent(actionId)}`, {
    method: 'DELETE',
  });
}

export function listUtilityUses(
  teamId: string,
  query: { map?: string; type?: string } = {},
): Promise<UtilityUseRecord[]> {
  const params = new URLSearchParams();
  if (query.map) params.set('map', query.map);
  if (query.type) params.set('type', query.type);
  const suffix = params.toString();
  const path = `/teams/${encodeURIComponent(teamId)}/utility-uses${suffix ? `?${suffix}` : ''}`;
  return apiFetch<UtilityUseRecord[]>(path);
}

export function createUtilityUse(teamId: string, body: UtilityWrite): Promise<UtilityUseRecord> {
  return apiFetch<UtilityUseRecord>(`/teams/${encodeURIComponent(teamId)}/utility-uses`, {
    method: 'POST',
    body,
  });
}

export function updateUtilityUse(
  teamId: string,
  utilityUseId: string,
  body: UtilityWrite,
): Promise<UtilityUseRecord> {
  return apiFetch<UtilityUseRecord>(
    `/teams/${encodeURIComponent(teamId)}/utility-uses/${encodeURIComponent(utilityUseId)}`,
    { method: 'PUT', body },
  );
}

export function deleteUtilityUse(teamId: string, utilityUseId: string): Promise<void> {
  return apiFetch<void>(
    `/teams/${encodeURIComponent(teamId)}/utility-uses/${encodeURIComponent(utilityUseId)}`,
    { method: 'DELETE' },
  );
}
